import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Fingerprint, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { PinInput } from "@/components/PinInput";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { bioSupported, registerBio, removeBio, bioLocalId, bioCancelled } from "@/lib/biometric";

export const BiometricSettings = () => {
  const { setUser, user } = useAuth();
  const [supported, setSupported] = useState(null);
  const [creds, setCreds] = useState([]);
  const [open, setOpen] = useState(false);
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const localId = bioLocalId();
  const here = creds.some((c) => c.credential_id === localId);

  const load = () => api.get("/webauthn/credentials").then((r) => {
    setCreds(r.data);
    setUser({ ...user, has_biometric: r.data.length > 0 });
  });
  useEffect(() => {
    bioSupported().then(setSupported);
    api.get("/webauthn/credentials").then((r) => setCreds(r.data));
  }, []);

  const enable = async (value) => {
    setBusy(true);
    setErr("");
    try {
      await registerBio(value);
      toast.success("Sidik jari aktif di perangkat ini");
      setOpen(false);
      await load();
    } catch (e) {
      setPin("");
      if (bioCancelled(e)) setErr("Pendaftaran sidik jari dibatalkan");
      else setErr(e?.response ? errMsg(e) : "Perangkat menolak pendaftaran sidik jari");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    try {
      await removeBio(id);
      toast.success("Sidik jari dihapus");
      await load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-4" data-testid="biometric-settings">
      <div className="flex items-center gap-3">
        <span className="h-9 w-9 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-700 text-white flex items-center justify-center"><Fingerprint className="h-5 w-5" /></span>
        <div className="flex-1">
          <div className="font-semibold text-slate-800">Keamanan Sidik Jari</div>
          <div className="text-[11px] text-slate-500">Buka aplikasi & konfirmasi transfer tanpa mengetik PIN. PIN tetap bisa dipakai.</div>
        </div>
      </div>
      {supported === false && !here && (
        <p className="mt-3 text-xs text-amber-700 bg-amber-50 rounded-lg p-2.5" data-testid="biometric-unsupported">Perangkat atau browser ini belum mendukung sidik jari. Coba buka aplikasi di HP yang memiliki sensor sidik jari.</p>
      )}
      {supported && !here && (
        <button onClick={() => { setPin(""); setErr(""); setOpen(true); }} data-testid="biometric-enable-btn"
          className="mt-3 w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-2.5 rounded-xl text-sm transition-colors">Aktifkan Sidik Jari</button>
      )}
      {here && <p className="mt-3 text-xs font-semibold text-emerald-700" data-testid="biometric-status-on">Aktif di perangkat ini</p>}
      {creds.length > 0 && (
        <div className="mt-3 space-y-2" data-testid="biometric-device-list">
          {creds.map((c) => (
            <div key={c.credential_id} className="flex items-center justify-between text-sm border border-slate-100 rounded-lg px-3 py-2">
              <div>
                <div className="font-medium text-slate-800">{c.device}{c.credential_id === localId && " (perangkat ini)"}</div>
                <div className="text-[11px] text-slate-500">Didaftarkan {fmtDate(c.created_at)}</div>
              </div>
              <button onClick={() => remove(c.credential_id)} data-testid={`biometric-remove-${c.credential_id.slice(0, 8)}`} className="p-2 text-slate-400 hover:text-red-600" aria-label="Hapus sidik jari"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
      )}
      <Dialog open={open} onOpenChange={(o) => !busy && setOpen(o)}>
        <DialogContent className="max-w-sm" data-testid="biometric-pin-dialog">
          <DialogHeader>
            <DialogTitle className="text-center">Aktifkan Sidik Jari</DialogTitle>
            <DialogDescription className="text-center">Masukkan PIN Anda, lalu tempelkan jari pada sensor HP.</DialogDescription>
          </DialogHeader>
          <PinInput value={pin} onChange={setPin} onComplete={enable} disabled={busy} testid="biometric-pin-input" />
          {err && <p className="text-sm text-center text-red-600 font-medium" data-testid="biometric-error">{err}</p>}
          <button onClick={() => enable(pin)} disabled={busy || pin.length !== 6} data-testid="biometric-pin-submit"
            className="w-full bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white font-semibold py-3 rounded-xl">{busy ? "Memproses..." : "Lanjut"}</button>
        </DialogContent>
      </Dialog>
    </div>
  );
};
